import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import React from 'react';
import { createRoot } from 'react-dom/client';
import styled, { css, ThemeProvider } from 'styled-components';
import {
  Blocker,
  DataClientProviderBridge,
  DataProvider,
  DesignSysEnvUtil,
  ErrorBoundary,
  LightTheme,
  LoggerUtil,
  MediaQueryUtil,
  ServiceWorkerProvider,
  Sidedrawer,
  Suspense,
  Toasts,
  ConsumeWarningsUtil,
  ErrorLayout,
  PendoInitializer,
} from '@axiom/ui';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router';

import { EnvUtil } from './utils/env-util';
import themes from './themes';
import { AppRouter } from './Router';
import { initializeStore } from './lib/store-manager';
// eslint-disable-next-line import/no-unresolved
import 'semantic-ui-less/semantic.less';
import '@axiom/ui/src/semantic-ui-components.less';
import '@axiom/ui/src/public/static/bootstrap.min.css';
import '@axiom/ui/src/public/static/main.css';
import '@axiom/ui/src/public/static/main-internal.css';
import '@axiom/ui/src/public/static/css-helpers.css';

/**
 * Temp disable until we can get to this ticket
 * APCORE-2484
 */
if (process.env.NODE_ENV !== 'production') {
  ConsumeWarningsUtil.consumePropWarnings();
}

const AppWrapper = styled.div`
  html {
    font-size: 1rem;
  }

  @media print {
    html {
      font-size: 1.25rem;
      padding-left: 0;
      padding-right: 0;
    }
    .row(.top-logo-header) {
      page-break-inside: avoid;
    }
  }

  hr {
    margin-top: 8px;
    margin-bottom: 0;
    background-color: rgb(0, 0, 0);
  }

  @page {
    margin-left: -50mm;
    margin-right: -50mm;
  }

  .fluid-padding {
    max-width: 1980px;
    margin: auto;
  }

  .fluid-padding > div:first-child,
  .fluid-padding > div:last-child {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  .popover {
    border-radius: 0.5rem !important;
    padding: 8px;
  }

  .no-margin {
    margin: 0px !important;
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    .hide-ie {
      display: none !important;
    }
  }

  ${MediaQueryUtil.smallScreenOnly(css`
    .panel {
      margin-bottom: 30px !important;
    }
    .fluid-padding > div {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
    .nav {
      display: block !important;
      text-align: left;
      padding: 1.2rem;
    }
    .nav li {
      max-width: 180px;
    }
    .notification-counter {
      display: none !important;
    }
  `)}
`;

DesignSysEnvUtil.initialize({
  COOKIE_DOMAIN: EnvUtil.cookieDomain,
  JWT_COOKIE_KEY: EnvUtil.userCookieName,
  GOOGLE_MAPS_API_URL: EnvUtil.googleMapsApiUrl,
  HUBSPOT_TALENT_FINDER: EnvUtil.hubspotTalentFinder,
  LOGIN_URL: EnvUtil.loginUrl,
  CLIENT_API_BASE: EnvUtil.clientApiBase,
  LOGO_DEV_BASE_URL: EnvUtil.logoDevBaseUrl,
  LOGO_DEV_PUBLISHABLE_KEY: EnvUtil.logoDevPublishableKey,
  S3_PUBLIC_BASE_URL: EnvUtil.publicBaseUrlS3,
  S3_PUBLIC_FILE_BUCKET: EnvUtil.publicS3Bucket,
  ENABLE_SUGGESTIONS: EnvUtil.enableSuggestions,
  ENABLE_CALENDAR_EVENTS_TODAY: EnvUtil.enableCalendarEventsToday,
});

if (EnvUtil.enableLogging) {
  LoggerUtil.initialize(
    EnvUtil.logglyClientId,
    'portal',
    EnvUtil.environmentName
  );
}

const store = initializeStore();

const Bootstrap = () => {
  const handleError = error => {
    const errorCode = error?.getStatusCode() ?? null;

    switch (errorCode) {
      case 401: {
        if (window.location.pathname !== '/401') {
          window.location.href = '/401';
        }
        break;
      }
      case 404: {
        if (window.location.pathname !== '/404') {
          window.location.href = '/404';
        }
        break;
      }

      default: {
        break;
      }
    }

    return <ErrorLayout />;
  };

  return (
    <AppWrapper>
      <Provider store={store}>
        <ThemeProvider theme={themes.main}>
          <DataProvider>
            <DataClientProviderBridge />
            <BrowserRouter future={{ v7_startTransition: true }}>
              <ErrorBoundary errorLayout={handleError}>
                <Blocker>
                  <Toasts />
                  <Suspense>
                    <PendoInitializer pendoAppId={EnvUtil.pendoAppId} />
                    <LightTheme backgroundName="none">
                      <Sidedrawer>
                        <ServiceWorkerProvider />
                        <AppRouter />
                      </Sidedrawer>
                    </LightTheme>
                  </Suspense>
                </Blocker>
              </ErrorBoundary>
            </BrowserRouter>
          </DataProvider>
        </ThemeProvider>
      </Provider>
    </AppWrapper>
  );
};

const root = createRoot(document.querySelector('#root'));
root.render(<Bootstrap />);
